import type { KvResponse, BulkReadResponse } from './client/types';

/**
 * Cache em memória pras keys prefetched no boot do AppRoot.
 *
 * Objetivo: trocar N GETs individuais de `usePersistedState` por 1 POST
 * `:bulk-read` no boot. O cache é consultado transparentemente via
 * `createAppData`; callers nunca chamam o cache diretamente.
 *
 * Máquina de estados:
 *   `disabled` → cache inativo; todo `get(key)` retorna `undefined` (caller
 *     faz GET). Inicial e após falha de prefetch.
 *   `unknown`  → `startPrefetch` disparou mas bulkRead ainda não resolveu.
 *     `get(key)` await a promise antes de decidir.
 *   `primed`   → bulkRead resolveu. `get(key)` retorna o valor se a key
 *     estava no prefetch (hit ou primed-absent=null); `undefined` se a key
 *     nunca foi declarada (fallback pro GET individual).
 *
 * Write-through: `set()` prime o cache, `delete()` invalida.
 *
 * Lifecycle: uma instância por `HookProvider` mount (memoizado em
 * `(appId, apiHost)`). Desmonta junto com o bundle — logout + app swap
 * limpam naturalmente.
 *
 * Fora de escopo: cross-tab sync, TTL, retry. Ver docs/06-data.md § Batch
 * read API.
 */
export type AppDataCacheState = 'disabled' | 'unknown' | 'primed';

export class AppDataCache {
  private _state: AppDataCacheState = 'disabled';
  private values = new Map<string, KvResponse>();
  private prefetchedKeys = new Set<string>();
  private prefetchPromise: Promise<void> | null = null;

  /**
   * Estado atual da máquina. Público pra guards de render como
   * `PersistedKeysPrefetch` saberem quando é seguro liberar children (evita
   * blink de `defaultValue` em `usePersistedState` — G77).
   */
  get state(): AppDataCacheState {
    return this._state;
  }

  /**
   * Dispara o prefetch. Idempotente: chamadas subsequentes enquanto o cache
   * já saiu de `disabled` são no-op (não re-fetch). Pra refresh, construir
   * um cache novo (unmount/remount do provider).
   *
   * Wave 5 #39: `opts.onError(err)` fires once when the fetcher rejects so
   * callers (template's `PersistedKeysPrefetch`) can emit observability
   * (PostHog event `app_data_cache_disabled` + Sentry breadcrumb). Cache
   * silently falls back to per-key GETs as before.
   */
  startPrefetch(
    keys: string[],
    fetcher: (keys: string[]) => Promise<BulkReadResponse>,
    opts?: {
      onError?: (err: Error) => void;
      onTimeout?: () => void;
      timeoutMs?: number;
    },
  ): void {
    if (this._state !== 'disabled') return;
    if (keys.length === 0) return;
    this._state = 'unknown';
    for (const k of keys) this.prefetchedKeys.add(k);

    let timeoutFired = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (opts?.onTimeout && opts.timeoutMs && opts.timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        timeoutId = null;
        if (this._state === 'unknown' && !timeoutFired) {
          timeoutFired = true;
          try { opts.onTimeout!(); } catch { /* swallow */ }
        }
      }, opts.timeoutMs);
    }

    this.prefetchPromise = fetcher(keys).then(
      ({ values }) => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        for (const [k, v] of Object.entries(values)) {
          this.values.set(k, { key: k, value: v.value, updated_at: v.updated_at });
        }
        this._state = 'primed';
      },
      (err: unknown) => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        this._state = 'disabled';
        this.prefetchedKeys.clear();
        this.prefetchPromise = null;
        if (opts?.onError) {
          const e = err instanceof Error ? err : new Error(String(err));
          try { opts.onError(e); } catch { /* swallow listener errors */ }
        }
      },
    );
  }

  /**
   * Resolve quando o cache deixa de ser `unknown`. Usado por guards de render
   * pra esperar o prefetch inicial antes de expor children que dependem de
   * `usePersistedState` — evita que o consumer observe `defaultValue` durante
   * o load window (G77).
   *
   * Retorno:
   *   `'primed'` — prefetch resolveu com sucesso; `get()` serve valores cached.
   *   `'disabled'` — prefetch falhou ou nunca foi disparado; `get()` retorna
   *     undefined e callers caem no GET individual.
   *   `'timeout'` — cache ainda `unknown` depois de `timeoutMs`. Caller deve
   *     liberar render mesmo assim (fallback pra não prender a UI).
   */
  waitForPrimed(timeoutMs: number): Promise<AppDataCacheState | 'timeout'> {
    if (this._state !== 'unknown') {
      return Promise.resolve(this._state);
    }
    if (!this.prefetchPromise) {
      return Promise.resolve(this._state);
    }
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<'timeout'>((resolve) => {
      timeoutId = setTimeout(() => resolve('timeout'), timeoutMs);
    });
    const settled = Promise.race([
      this.prefetchPromise.then(() => this._state),
      timeoutPromise,
    ]);
    return settled.finally(() => {
      if (timeoutId !== null) clearTimeout(timeoutId);
    });
  }

  /**
   * Retorno:
   *   `undefined` — cache disabled OU key não estava no prefetch → caller
   *     deve fazer GET individual.
   *   `null` — key estava no prefetch mas backend não tinha row (miss) →
   *     caller pode tratar como default.
   *   `KvResponse` — cache hit.
   */
  async get(key: string): Promise<KvResponse | null | undefined> {
    if (this._state === 'disabled') return undefined;
    if (this._state === 'unknown') {
      await this.prefetchPromise?.catch(() => {});
    }
    if (this._state !== 'primed') return undefined;
    if (!this.prefetchedKeys.has(key)) return undefined;
    return this.values.get(key) ?? null;
  }

  /**
   * Chamado após `set()` bem-sucedido. Trata a key como cached daqui pra
   * frente (próximos `get` devolvem essa resposta sem network).
   */
  primeOne(key: string, res: KvResponse): void {
    this.values.set(key, res);
    this.prefetchedKeys.add(key);
  }

  /**
   * Chamado após `delete()` bem-sucedido. Próximo `get` cai na network
   * (retorna 404/null).
   */
  invalidate(key: string): void {
    this.values.delete(key);
    this.prefetchedKeys.delete(key);
  }
}
